import { uploadUrlResponseSchema } from "@boark/shared/types/upload-url-response";

import { FETCH_ERROR } from "../consts/fetch-error";

const API_BASE_URL = process.env.API_BASE_URL || "/api";

export async function getUploadUrl({
  filename,
  path,
  isPublic = false,
}: {
  filename: string;
  path?: string;
  isPublic?: boolean;
}): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/upload-url`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      name: filename,
      ...(path ? { path } : {}),
      ...(isPublic ? { isPublic } : {}),
    }),
  });

  if (response.status === 401) {
    throw FETCH_ERROR.UNAUTHORIZED;
  }

  const data = (await response.json()) as unknown;
  return uploadUrlResponseSchema.parse(data).url;
}

export type UploadOptions = {
  key: string;
  secret: string;
  bucket: string;
};

export async function uploadDataUriToMinio(
  dataURI: string,
  isPublic: boolean = false,
) {
  const blob =
    dataURI.match(/^blob:http(?:s)?:\/\//) !== null
      ? await (await fetch(dataURI)).blob()
      : dataURLtoBlob(dataURI);

  const fileType = blob.type.split("/").slice(-1)[0];
  const fileName = `${generateRandomFileName()}.${fileType}`;

  const file = blobToFile(blob, fileName);
  return await uploadFileToMinio(file, isPublic);
}

export async function uploadFileToMinio(file: File, isPublic: boolean = false) {
  const { name } = file;

  const uploadUrl = await getUploadUrl({ filename: name, isPublic });

  const response = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
  });

  if (!response.ok) {
    throw new Error("Failed to upload file");
  }

  const url = new URL(uploadUrl);
  const uploadUrlWithoutQueryString = `${url.origin}${url.pathname}`;

  return uploadUrlWithoutQueryString;
}

function dataURLtoBlob(dataURI: string) {
  // convert base64 to raw binary data held in a string
  // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
  const byteString = atob(dataURI.split(",")?.[1] ?? "");

  // separate out the mime component
  const mimeString = dataURI.split(",")?.[0]?.split(":")?.[1]?.split(";")?.[0];

  // write the bytes of the string to an ArrayBuffer
  const ab = new ArrayBuffer(byteString.length);

  // create a view into the buffer
  const ia = new Uint8Array(ab);

  // set the bytes of the buffer to the correct values
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  // write the ArrayBuffer to a blob, and you're done
  const blob = new Blob([ab], { type: mimeString });
  return blob;
}

function blobToFile(blob: Blob, name: string) {
  const memeType = blob.type;
  return new File([blob], name, {
    type: memeType,
  });
}

function generateRandomFileName() {
  return `${new Date().toISOString()}-${Math.random().toString(36).substring(2, 15)}`;
}
